import { getWorksheetBatchUpdateDefinition } from 'Components/WorksheetBatchUpdate';
import { ApiErrorCodes } from 'Data/Helpers/errorHelpers';
import { RootState } from 'Data/Redux';
import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { BatchUpdateSubmissionModel } from 'Data/ViewModels/BatchUpdateRequestModel';
import { IDataSyncModel } from 'Data/ViewModels/IDataSyncModel';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { map } from 'ramda';
import { Epic, StateObservable, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { actions } from '.';
import * as oPath from '../../../Lib/Object/Path/Immutable';
import { addErrorNotice, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { getWorksheetStatusPatch } from '../../Helpers/worksheetHelpers';
import { ISyncPatch } from '../DataSync/types';
import { callApiBatchCheckWorksheets, callApiBatchUpdateWorksheets, callApiGetWorksheets } from './apiCalls';
import { hasImportMismatch, remapQueryParamsForApiRequest } from './helpers';
import { ApplyUpdateAction, ApplyUpdateConflictResolveAction, CheckSelectedWorksheetsAction } from './types';

/**
 * Respond to a refreshWorksheets action by validating the current worksheet query
 * and, if valid, calling the api to fetch the worksheets
 */
export const refreshWorksheets: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.refreshWorksheets.toString()),
    switchMap(() => {
      // Ignore refresh actions if we aren't ready to query for worksheets
      const query = state$.value.worksheetBatchUpdate.query.currentQuery;
      const typeId = state$.value.worksheetBatchUpdate.typeId;

      return state$.value.worksheetBatchUpdate.status.initialised && typeId && query && query.isValid
        ? callApiGetWorksheets(
            state$.value.worksheetBatchUpdate.worksheetType,
            remapQueryParamsForApiRequest(typeId!, query.params)
          ).pipe(
            switchMap((worksheets) =>
              of(
                actions.refreshWorksheetsResolve({
                  worksheets,
                  query,
                })
              )
            ),
            catchWithAuthErrors((err: any) =>
              of(actions.refreshWorksheetsReject(), addErrorNotice('Failed to retrieve worksheets.', err))
            )
          )
        : of(actions.refreshWorksheetsResolve({ worksheets: [], query }));
    })
  );

/**
 * Trigger a refreshWorksheets action if we have updated the current query and it doesn't match the previously fired value
 */
export const refreshOnQueryChanges: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.updateCurrentQuery.toString()),
    filter(
      () =>
        !deepEquals(
          state$.value.worksheetBatchUpdate.query.currentQuery,
          state$.value.worksheetBatchUpdate.query.lastQuery
        )
    ),
    switchMap(() => of(actions.refreshWorksheets()))
  );

/**
 * Update the current state of the query when any action that will change a part of it has been fired
 */
export const updateQueryOnChange: Epic = (action$) =>
  action$.pipe(
    ofType(actions.configure.toString(), actions.setQueryValue.toString(), actions.setUriParams.toString()),
    filter((a) => a.type !== actions.setQueryValue.toString() || a.payload.sync),
    switchMap(() => of(actions.updateCurrentQuery()))
  );

/**
 * Respond to an applyUpdate action by making an API request to update these worksheets
 * with the models (that have been patched with the entered update data)
 */
export const applyUpdate: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.applyUpdate.toString()),
    switchMap((action: ApplyUpdateAction) => {
      const worksheetType = state$.value.worksheetBatchUpdate.worksheetType;
      const updateData = state$.value.worksheetBatchUpdate.updateData;
      const selectedWorksheets = Object.fromEntries(
        Object.entries(state$.value.worksheetBatchUpdate.worksheets).filter(
          ([k, _]) => state$.value.worksheetBatchUpdate.worksheetsState[k].selected
        )
      );

      const typeId = state$.value.worksheetBatchUpdate.typeId;
      if (!hasValue(typeId)) throw new Error(`Unrecognised worksheet type ${typeId}.`);

      const defn = getWorksheetBatchUpdateDefinition(typeId!);

      const importSourceFields = Object.entries(state$.value.worksheetBatchUpdate.updateData).filter(
        ([k, _]) => defn.worksheetFields[k]?.isImportSource
      );

      const updates: Array<BatchUpdateSubmissionModel> = Object.entries(selectedWorksheets).map(
        ([worksheetId, worksheet]) => ({
          Id: worksheetId,
          Test: {
            ...worksheet.syncModel,
            syncConflictBaseData: action.payload.includeConflictResolutionData
              ? (worksheet.syncModel_synced as IDataSyncModel).data
              : undefined,
          },
          Imports: importSourceFields
            .filter(([k, v]) => {
              // Filter out import sources where we don't require a re-import
              // Note if the results on the import source became mismatched since worksheets were loaded,
              // this will not trigger a reimport. May be reconsidered after QP-6156.
              let currentImport = oPath.get(worksheet.syncModel_synced, k);

              return (
                hasNonEmptyValue(currentImport) &&
                currentImport?.Id === v?.Id &&
                hasImportMismatch(typeId!, worksheet.syncModel_synced, k)
              );
            })
            .map(([_, v]) => ({ Id: v?.Id })),
        })
      );

      return callApiBatchUpdateWorksheets(worksheetType, updates).pipe(
        switchMap((response: Array<IDataSyncModel>) => {
          let syncPatches = getSyncPatchesFromResponse(response, selectedWorksheets);
          if (response.some((w) => w.hasSyncConflict))
            return of(
              actions.applyUpdateResolve({
                syncPatches: {},
                updateData,
              }),
              actions.applyUpdateConflict({ syncPatches })
            );

          return of(
            actions.applyUpdateResolve({
              syncPatches,
              updateData,
            })
          );
        }),
        catchWithAuthErrors((err: any) => {
          if (err.code === ApiErrorCodes.ClientErrorConflict) {
            if (action.payload.includeConflictResolutionData) {
              // This scenario shouldn't occur as this error code is only raised when we don't include resolution data
              // But to avoid a possible infinite loop, throw an error if we get here
              return of(
                actions.applyUpdateReject(),
                addErrorNotice('Failed to automatically resolve conflict for batch update.', err)
              );
            }

            // Silently retry the update, providing the data the server can use to resolve the conflict
            return of(actions.applyUpdateReject(), actions.applyUpdate({ includeConflictResolutionData: true }));
          }

          return of(actions.applyUpdateReject(), addErrorNotice('Failed to apply update to worksheets.', err));
        })
      );
    }),
    catchError((err) => of(actions.applyUpdateReject(), addErrorNotice('Failed to apply update to worksheets.', err)))
  );

const getSyncPatchesFromResponse = (
  response: Array<IDataSyncModel>,
  originalWorksheets: { [worksheetId: string]: any }
): { [worksheetId: string]: ISyncPatch } => {
  let syncPatches = map<{ [worksheetId: string]: any }, ISyncPatch>(
    (w) => ({
      clientModelBefore: w.syncModel,
      serverModelSent: w.syncModel,
      syncParams: w.syncParams,
    }),
    originalWorksheets
  );

  response.forEach((w) => {
    syncPatches[w.data.Id].serverModelReceived = w;
  });

  return syncPatches;
};

/**
 * Resolves an update conflict using the nominated strategy
 */
export const updateConflictResolve: Epic = (action$) =>
  action$.pipe(
    ofType(actions.applyUpdateConflictResolve.toString()),
    switchMap((action: ApplyUpdateConflictResolveAction) => {
      switch (action.payload.choice) {
        case 'client':
          // Reapply update to overwrite conflicting changes
          // In this scenario, the worksheet version we have is already out of date
          // (as worksheets with conflicts are returned in their state before changes are saved)
          // so we know we need to provide the server with the data to resolve a conflict
          return of(
            actions.applyUpdate({
              includeConflictResolutionData: true,
            })
          );
        case 'server':
          // Refresh worksheets to reload from the server and discard unmerged changes.
          // We only need to do this as the current worksheets do not match their latest version
          // (as worksheets with conflicts are returned in their state before changes are saved)
          return of(actions.refreshWorksheets());
        default:
          return of(
            actions.applyUpdateReject(),
            noticeActions.addNotice({
              severity: 'error',
              message: `Failed to resolve conflict for batch update. Unrecognised strategy ${action.payload.choice}.`,
            })
          );
      }
    })
  );

/**
 * Responds to action updateAllWorksheetStatus by setting the status property of all worksheets
 * (based on the current worksheet model and the current user)
 */
export const updateAllWorksheetStatus: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.updateAllWorksheetStatus.toString()),
    switchMap(() => {
      const {
        value: {
          worksheetBatchUpdate: { worksheets },
          user,
        },
      } = state$;

      const worksheetStatusPatches = Object.entries<any>(worksheets).map(([worksheetId, value]) => {
        const { syncModel: worksheet, status } = value;
        return { worksheetId, status: getWorksheetStatusPatch(worksheet, user, status) };
      });

      return of(actions.patchWorksheetStatus({ worksheetStatusPatches }));
    })
  );

/**
 * Trigger an update to worksheet status after an action that has updated the set of worksheets
 */
export const updateAllWorksheetStatusOnRefreshResolve: Epic = (action$) =>
  action$.pipe(
    ofType(
      actions.refreshWorksheetsResolve.toString(),
      actions.applyUpdateResolve.toString(),
      actions.checkSelectedWorksheetsResolve.toString()
    ),
    switchMap(() => of(actions.updateAllWorksheetStatus()))
  );

/**
 * Post an API call to uncheck any worksheets that we're updating, then follows it up with
 * the batch update.
 */
export const uncheckWorksheetsAndUpdate: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.uncheckWorksheetsAndUpdate.toString()),
    switchMap(() => {
      const {
        value: {
          worksheetBatchUpdate: { worksheets, worksheetsState },
        },
      } = state$;
      const worksheetsToUncheck = Object.entries(worksheets)
        .filter(
          ([worksheetId, worksheet]) =>
            //locked, but can be unchecked
            worksheet.status.readonly.locked &&
            worksheet.status.readonly.uncheckAllowed &&
            //and selected for update
            worksheetsState[worksheetId].selected
        )
        .map(([worksheetId, _]) => worksheetId);

      return callApiBatchCheckWorksheets({ Ids: worksheetsToUncheck, Person: null, Date: null }).pipe(
        switchMap(() => {
          //sucessfully unchecked worksheets, we can now apply the batch update
          //unchecking the worksheets will result in new versioning info, but haven't made the API
          //return the new versions ... i.e. we can expect a conflict, which the server can likely resolve
          //on its own.
          return of(actions.applyUpdate({ includeConflictResolutionData: true }));
        }),
        catchWithAuthErrors((err: any) => {
          //failed to uncheck worksheets, display an error.
          return of(actions.applyUpdateReject(), addErrorNotice('Failed to uncheck worksheets.', err));
        })
      );
    })
  );

/**
 * Post an API call to update the checked status of selected worksheets
 */
export const checkWorksheets: Epic = (action$, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType(actions.checkSelectedWorksheets.toString()),
    switchMap((a: CheckSelectedWorksheetsAction) => {
      const {
        value: {
          worksheetBatchUpdate: { worksheets, worksheetsState },
        },
      } = state$;

      const {
        payload: { checkedByPerson, checkedDate },
      } = a;

      const selectedWorksheetIds = Object.entries(worksheets)
        .filter(([k, _]) => worksheetsState[k].selected)
        .map(([k, _]) => k);

      return callApiBatchCheckWorksheets({
        Ids: selectedWorksheetIds,
        Person: checkedByPerson,
        Date: checkedDate,
      }).pipe(
        switchMap(() => {
          return of(
            actions.checkSelectedWorksheetsResolve({ checkedByPerson, checkedDate, worksheetIds: selectedWorksheetIds })
          );
        }),
        catchWithAuthErrors((err: any) => {
          return of(actions.checkSelectedWorksheetsReject(), addErrorNotice('Failed to check worksheets.', err));
        })
      );
    })
  );
